/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, ReactNode, useImperativeHandle, useState} from 'react';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import {makeStyles} from '@material-ui/core/styles';
import classNames from 'classnames';
import {MobileDrawer} from '@ion/dls/src/components/drawer/MobileDrawer';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {MaterialMoreVertIcon} from '@ion/dls/src/icons/MaterialMoreVertIcon';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';

export interface CellMenuType {
  menuItems: ReactNode;
  className?: string;
  mobileDrawerZindex?: number;
}

export enum MoreMenuOptions {
  editAsset = 'editAsset',
  manageAssociations = 'manageAssociations',
  viewAuditLog = 'viewAuditLog',
  delete = 'delete'
};

const useStyles = makeStyles(theme => ({
  cellButtonRoot : {
    height    : '40px',
    marginTop : '-2px',
    minWidth  : 'unset !important',
    '&:hover' : {
      backgroundColor : 'transparent'
    }
  },
  // the current theme's row menu - the DLS menu's own rounded, bordered
  // panel, as the filter drawer's favorites menu is ...
  currentMenuPaper : {
    '&&' : {
      border       : `1px solid ${dlsColor('grey5')}`,
      borderRadius : 12,
      boxShadow    : 'none',
      boxSizing    : 'border-box',
      filter       : 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))'
    }
  }
}));

/**
 * @CellMenu
 *
 * CellMenu showing inside Grid
 *
 *     <CellMenu ref={cellMenuRef} menuItems={
 *       <div>
 *         <CellMenuItem icon={<MapEditIcon />}
 *           onClick={editAsset}
 *           label={translate('admin-assets:grid.menu.editAsset')} />
 *         <CellMenuItem icon={<SystemDeleteIcon />}
 *           onClick={deleteAsset}
 *           label={translate('admin-assets:grid.menu.delete')} />
 *       </div>
 *     } />
 */
export const CellMenu = forwardRef(({menuItems, className, mobileDrawerZindex}: CellMenuType, ref) => {
  const classes     = useStyles();
  const isPhone     = useIsPhone();
  const {isCurrent} = useThemeContext();

  const [anchorMenuEl, setAnchorMenuEl]       = useState(null);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    closeCellMenu : toggleMenu
  }));

  function toggleMenu({currentTarget = {}} = {}, reason) {
    const isOpen = !reason;
    if (!isPhone) {
      setAnchorMenuEl(isOpen ? currentTarget : null);
    } else {
      setBottomSheetOpen(!bottomSheetOpen);
    }
  };

  return (
    <>
      <Button onClick={toggleMenu}
        className={classNames(classes.cellButtonRoot, className)}
        disableRipple>
        <MaterialMoreVertIcon />
      </Button>
      {!isPhone ? (
        <Menu anchorEl={anchorMenuEl}
          ref={ref}
          getContentAnchorEl={null}
          keepMounted
          anchorOrigin={{
            vertical   : 'center',
            horizontal : 'right'
          }}
          transformOrigin={{
            vertical   : 'top',
            horizontal : 'right'
          }}
          open={Boolean(anchorMenuEl)}
          classes={isCurrent ? {paper: classes.currentMenuPaper} : undefined}
          onClose={toggleMenu}>
          {anchorMenuEl ? menuItems : undefined}
        </Menu>
      )
        :(
          <MobileDrawer open={bottomSheetOpen} onClose={toggleMenu} mobileDrawerZindex={mobileDrawerZindex}>
            {menuItems}
          </MobileDrawer>
        )}
    </>
  );
});

export default CellMenu;
